import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, ErrorBanner } from '@saas-kit/ui';

import type { AdminSetting, SettingValue } from './api';
import { parseDraft, toDraft, valuesEqual, type SettingDraft } from './draft';
import { SettingValueEditor } from './SettingValueEditor';

interface Props {
  setting: AdminSetting;
  isSaving: boolean;
  onSave: (key: string, value: SettingValue) => Promise<void>;
}

export function SettingRow({ setting, isSaving, onSave }: Props): JSX.Element {
  const { t, i18n } = useTranslation('adminSettings');
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<SettingDraft>(() => toDraft(setting.value));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!editing) {
      setDraft(toDraft(setting.value));
      setError(null);
    }
  }, [setting.value, editing]);

  const dateFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(i18n.language, {
        dateStyle: 'medium',
        timeStyle: 'short',
      }),
    [i18n.language],
  );

  async function handleSave(): Promise<void> {
    const parsed = parseDraft(setting.key, draft);
    if (!parsed.ok) {
      setError(t('errors.invalid_int'));
      return;
    }
    if (valuesEqual(parsed.value, setting.value)) {
      setEditing(false);
      return;
    }
    setError(null);
    try {
      await onSave(setting.key, parsed.value);
      setEditing(false);
    } catch {
      // toast handled upstream
    }
  }

  function handleCancel(): void {
    setDraft(toDraft(setting.value));
    setError(null);
    setEditing(false);
  }

  return (
    <div className="flex flex-col gap-3 py-4 sm:grid sm:grid-cols-[minmax(0,1.2fr)_minmax(0,1.6fr)_minmax(0,1fr)] sm:items-start sm:gap-6">
      <div className="min-w-0">
        <div className="font-mono text-sm">{setting.key}</div>
      </div>

      <div className="min-w-0 space-y-2">
        {editing ? (
          <>
            <SettingValueEditor
              valueKey={setting.key}
              draft={draft}
              onChange={setDraft}
              disabled={isSaving}
            />
            {error ? <ErrorBanner>{error}</ErrorBanner> : null}
          </>
        ) : (
          <DisplayValue value={setting.value} />
        )}
      </div>

      <div className="flex items-start justify-between gap-3 sm:flex-col sm:items-end">
        <div className="text-end text-xs text-muted-foreground">
          <div>
            {setting.updated_by_user_id
              ? dateFormatter.format(new Date(setting.updated_at))
              : t('meta.never_updated')}
          </div>
          {setting.updated_by_user_id ? (
            <div className="break-all font-mono text-[10px]">
              {setting.updated_by_user_id}
            </div>
          ) : null}
        </div>

        {editing ? (
          <div className="flex gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleCancel}
              disabled={isSaving}
            >
              {t('actions.cancel')}
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={() => {
                void handleSave();
              }}
              disabled={isSaving}
            >
              {t('actions.save')}
            </Button>
          </div>
        ) : (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => setEditing(true)}
          >
            {t('actions.edit')}
          </Button>
        )}
      </div>
    </div>
  );
}

function DisplayValue({ value }: { value: SettingValue }): JSX.Element {
  const { t } = useTranslation('adminSettings');
  if (typeof value === 'boolean') {
    return (
      <span className="text-sm">
        {value ? t('value.true') : t('value.false')}
      </span>
    );
  }
  if (value === null || value === undefined) {
    return <span className="text-sm text-muted-foreground">{t('value.null')}</span>;
  }
  return <span className="font-mono text-sm">{String(value)}</span>;
}
